// ---------------------------------------------------------------------------
// RealtimeChannelLike — generic interface matching Supabase RealtimeChannel
// No direct @supabase/supabase-js dependency (same pattern as SupabaseLikeClient)
// ---------------------------------------------------------------------------

/** Minimal subset of Supabase RealtimeChannel used by realtime actions. */
export interface RealtimeChannelLike {
  on(event: string, opts: Record<string, unknown>, callback: (payload: unknown) => void): RealtimeChannelLike
  subscribe(callback?: (status: string, err?: Error) => void): RealtimeChannelLike
  unsubscribe(): Promise<'ok' | 'timed out' | 'error'>
  send(payload: { type: string; event: string; payload: Record<string, unknown> }): Promise<'ok' | 'error'>
  track(state: Record<string, unknown>): Promise<'ok' | 'error'>
  untrack(): Promise<'ok' | 'error'>
  presenceState<T = Record<string, unknown>>(): Record<string, T[]>
}

// ---------------------------------------------------------------------------
// SubscriptionRegistry — lifecycle manager for realtime channels
// ---------------------------------------------------------------------------

/**
 * Manages realtime channel subscriptions with a configurable channel limit.
 * Prevents subscription leaks by enforcing a maximum number of concurrent channels.
 */
export class SubscriptionRegistry {
  private channels = new Map<string, RealtimeChannelLike>()
  private readonly maxChannels: number

  constructor(maxChannels = 10) {
    this.maxChannels = maxChannels
  }

  /**
   * Add or replace a channel subscription.
   * Throws if the channel limit would be exceeded by a new (non-existing) name.
   */
  add(name: string, channel: RealtimeChannelLike): void {
    // Replacing existing channel is always allowed
    if (!this.channels.has(name) && this.channels.size >= this.maxChannels) {
      console.warn(
        `[SubscriptionRegistry] Maximum ${this.maxChannels} channels reached. Cannot add "${name}".`,
      )
      throw new Error(
        `Maximum ${this.maxChannels} channels reached. Remove a channel before adding "${name}".`,
      )
    }
    this.channels.set(name, channel)
  }

  /**
   * Remove and unsubscribe a channel by name. No-op if name does not exist.
   */
  async remove(name: string): Promise<void> {
    const ch = this.channels.get(name)
    if (!ch) return
    await ch.unsubscribe()
    this.channels.delete(name)
  }

  /**
   * Unsubscribe all channels and clear the registry.
   * Uses Promise.allSettled so one failure does not block others.
   */
  async unsubscribeAll(): Promise<void> {
    const tasks = Array.from(this.channels.values()).map((ch) => ch.unsubscribe())
    await Promise.allSettled(tasks)
    this.channels.clear()
  }

  /** Get a channel by name, or undefined if not found. */
  get(name: string): RealtimeChannelLike | undefined {
    return this.channels.get(name)
  }

  /** Number of active channel subscriptions. */
  get size(): number {
    return this.channels.size
  }

  /** Array of active channel names. */
  get activeChannels(): string[] {
    return Array.from(this.channels.keys())
  }
}
